/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */

import {
  CapNames,
  Director,
  Stage,
  DefaultCaps,
  DefaultNavigators,
  Materials,
  CameraDesc,
  FloorKind,
  SceneCompass,
  FloorDesc,
  CapDataDesc,
} from '@sick-davinci/glow';
import {
  CrownViewCapData,
} from '@sick-davinci/glow-crown';
import { CrownConnectionWebSocket, CrownProtocolMsgPack, LogLevel } from '@sick-davinci/device-communication';
import { Mesh, BoxBufferGeometry, Vector3 } from 'three';

import { DropDown } from '@sick-davinci/basic-elements';

export const dependencies = [DropDown];  // avoid wepack removal

/**
 * Demo:    CrownUpdateCamera
 * Description:
 *   This demo application shows how the camera settings are set with the Crown iface.
 *   Use the included app with an AppEngine to see the demo in action.
 *   Without the app the user can manually set the camera position and target using the UI elements.
 *   If connected the app let the camera rotates around the center.
 * Features:
 *  - Orbit navigation
 *  - Capability handling
 *  - Scene compass
 *  - ViewCrown / WebSocket connection
 *  - Native threejs code
 */
export const run = ((viewParent: HTMLElement) => {

  // * Camera values which can be toggled via UI
  const froms: any = {
    front: [0, 20, 100],
    back: [0, 20, -100],
    left: [-100, 20, 0],
    right: [100, 20, 0],
    bottom: [0, -100, 0],
    top: [0, 100, 0],
  };
  const tos: any = {
    middle: [0, 0, 0],
    left: [-20, 0, 0],
    right: [20, 0, 0],
    bottom: [0, -20, 0],
    top: [0, 20, 0],
  };

  // ** setup the stage *
  const camDesc: CameraDesc = {
    name: 'default',
    from: froms.front,
    to: tos.middle,
    up: [0, 1, 0],
    fov: 50,
  };

  const stage = new Stage(viewParent, {
    cameras: [camDesc],
    scenes: [{ name: 'default' }],
    viewports: [{
      name: 'default',
      scene: 'default',
      camera: 'default',
      navigator: 'orbit',
      bgcolor: 0x393939,
    }],
  });

  // * Add the scene compass for better orientation
  SceneCompass.create(stage, 'default', 'default');

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit');

  // * Data handling using capabilities
  const director = new Director(stage);

  DefaultCaps.init(director);

  // * Add some light so there is smth to see
  director.handle(CapNames.LIGHT, { lightType: 'point', color: 0xfffff0, orientation: { position: [1500, 800, -1300] } });
  director.handle(CapNames.LIGHT, { lightType: 'ambient', color: 0x888888 });

  // * Add a floor for better orientation
  director.handle(CapNames.FLOOR, {
    payload: {
      floor: FloorKind.WIREFRAME,
      size: 256,
      segments: 128,
      color: 0x555555,
    },
    orientation: {
      position: [0, -.01, 0],
    },
  } as CapDataDesc<FloorDesc>);

  // * Add 2 boxes using native threejs code
  stage.applyThreeDirect((scene) => {
    const box1 = createBox(0xe0e0e0, [10, 10, 10], [-10, 5, 0]);
    box1.name = 'box1';
    const box2 = createBox(0xe0e0e0, [10, 10, 10], [10, 5, 0]);
    box2.name = 'box2';
    scene.add(box1, box2);
  });

  stage.start();  // start visualizing data

  // ** Connect to the device via the ViewCrown using the provided ip and view-id
  const cc = new CrownConnectionWebSocket('127.0.0.1', 'crownMSGPACK', 3000);
  const cp = new CrownProtocolMsgPack();
  const view = new CrownViewCapData(cc, cp, '3D');

  // ** Set all necessary callback functions
  view.onPresentStart(() => stage.lockRenderer());
  view.onPresentEnd(() => stage.unlockRenderer());
  view.onPresentClear(() => stage.clearAllScenes());
  view.onPresentRemove((id) => stage.clearObject(stage.getObjectIdByName(id)));
  view.onPresentCameraCamDesc('primary', (cd) => {
    stage.changeCamera(cd);
  });
  view.onPresentAddCapData((capData) => {
    director.handleCapData(capData);
  });
  view.onConnectionChanged((connected) => {
    view.logMessage(LogLevel.ERROR, 'Viewer connected:' + connected);
    Array.from(document.getElementById('gui').children).forEach(((node) => (node as DropDown).disabled = connected)); // disconnect manual settings if driven by AE
  });

  // ** setup user interaction *
  {
    const camPosSel = document.getElementById('campos') as DropDown;
    camPosSel.addEventListener('change', ((evt) => {
      const val = (evt as any).detail;
      camDesc.from = froms[val];
      stage.changeCamera(camDesc, 1);
    }), true);
    camDesc.from = froms[camPosSel.value];
  }

  {
    const camTargetSel = document.getElementById('camtarget') as DropDown;
    camTargetSel.addEventListener('change', ((evt) => {
      const val = (evt as any).detail as string;
      if (val.startsWith('box') ) {
        const box = stage.getObjectByName(val);
        const t = new Vector3();
        box.getWorldPosition(t);
        camDesc.to = t.toArray();
      } else {
        camDesc.to = tos[val];
      }
      stage.changeCamera(camDesc, 1);

    }), true);
    camDesc.to = tos[camTargetSel.value];
  }
  stage.changeCamera(camDesc);
});

function createBox(color: number, size: [number, number, number], position: [number, number, number]) {
  const box =
    new Mesh(
      new BoxBufferGeometry(...size),
      Materials.create({ type: 'normal' }),
    );
  box.position.set(...position);
  box.updateMatrix();
  return box;
}
